import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../App';
import { useSupabase } from '../contexts/SupabaseContext';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Avatar, AvatarFallback } from './ui/avatar';
import { Users, Search, MessageCircle, UserPlus, Clock, CheckCircle, X, Sparkles, Coins } from 'lucide-react';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';
import QuickNav from './QuickNav';
import ThreadChat from './ThreadChat';
import { ensureUserExists, getExcludedUserIds } from '../services/supabaseFriendChat';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FriendsWithChat = () => {
  const { user } = useContext(AuthContext);
  const { 
    sendFriendRequest, 
    fetchFriendRequests, 
    acceptFriendRequest, 
    rejectFriendRequest,
    fetchFriends,
    openConversation,
    subscribeToUserFriendRequests,
    unsubscribe,
    pendingRequests,
    friends: supabaseFriends,
    loading: supabaseLoading
  } = useSupabase();
  
  const navigate = useNavigate();
  const [friends, setFriends] = useState([]);
  const [friendRequests, setFriendRequests] = useState([]);
  const [discoveredUsers, setDiscoveredUsers] = useState([]);
  const [selectedChat, setSelectedChat] = useState(null);
  const [activeTab, setActiveTab] = useState('friends');
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [sendingRequest, setSendingRequest] = useState({});

  // Initialize user in Supabase and fetch data
  useEffect(() => {
    if (user?.id && user?.full_name) {
      initializeAndFetch();
    }
  }, [user]);

  // Subscribe to real-time friend requests
  useEffect(() => {
    if (!user?.id) return;

    const channel = subscribeToUserFriendRequests(user.id, (newRequest) => {
      toast.info('New friend request received!');
      loadFriendRequests();
    });

    return () => {
      if (channel) unsubscribe(channel);
    };
  }, [user?.id]);

  // Sync Supabase state to local state
  useEffect(() => {
    setFriendRequests(pendingRequests);
  }, [pendingRequests]);

  useEffect(() => {
    setFriends(supabaseFriends);
    setLoading(supabaseLoading);
  }, [supabaseFriends, supabaseLoading]);

  const initializeAndFetch = async () => {
    setLoading(true);
    
    // Ensure current user exists in Supabase
    await ensureUserExists(user.id, user.full_name, user.avatar_url || null);
    
    // Fetch all data
    await Promise.all([
      loadFriendRequests(),
      loadFriends(),
      fetchDiscoveredUsers()
    ]);
    
    setLoading(false);
  };

  const loadFriendRequests = async () => {
    if (!user?.id) return;
    await fetchFriendRequests(user.id);
  };

  const loadFriends = async () => {
    if (!user?.id) return;
    await fetchFriends(user.id);
  };

  // Discovery from MongoDB API with Supabase exclusion filtering
  const fetchDiscoveredUsers = async (search = '') => {
    try {
      // Step 1: Fetch users from MongoDB with optional search
      const url = search 
        ? `${API}/friends/discover?search=${encodeURIComponent(search)}`
        : `${API}/friends/discover`;
        
      const response = await axios.get(url);
      if (!response.data.users) {
        setDiscoveredUsers([]);
        return;
      }

      const mongoUsers = response.data.users;

      // Step 2: Get excluded IDs from Supabase (friends + friend_requests)
      const { excludedIds } = await getExcludedUserIds(user?.id);

      // Step 3: Filter out excluded users
      const filteredUsers = mongoUsers.filter(u => !excludedIds.includes(u.id));

      setDiscoveredUsers(filteredUsers);
    } catch (error) {
      console.error('Error discovering users:', error);
      setDiscoveredUsers([]);
    }
  };

  const handleSendFriendRequest = async (receiverId, receiverName) => {
    if (!user?.id) {
      toast.error('Please log in to send friend requests');
      return;
    }

    setSendingRequest(prev => ({ ...prev, [receiverId]: true }));

    // Ensure receiver exists in Supabase
    await ensureUserExists(receiverId, receiverName, null);

    const result = await sendFriendRequest(user, receiverId);

    setSendingRequest(prev => ({ ...prev, [receiverId]: false }));

    if (result.success) {
      // Remove user from discovered list
      setDiscoveredUsers(prev => prev.filter(u => u.id !== receiverId));
    }
  };

  const handleAcceptRequest = async (requestId, requesterId, requesterName) => {
    if (!user?.id) return;

    const result = await acceptFriendRequest(user, requestId, requesterId, requesterName);
    
    if (result.success) {
      // Refresh discovered users to remove accepted friend
      fetchDiscoveredUsers();
    }
  };

  const handleRejectRequest = async (requestId) => {
    if (!user?.id) return;
    await rejectFriendRequest(user, requestId);
  };

  const openChat = async (friendData) => {
    if (!user?.id || !friendData?.friend?.id) {
      toast.error('Unable to open chat');
      return;
    }

    const friendId = friendData.friend.id;
    const friendName = friendData.friend.name || 'Friend';

    // Get or create conversation (lazy creation)
    const result = await openConversation(user, friendId);

    if (result.success) {
      setSelectedChat({
        conversationId: result.data.id,
        friendId: friendId,
        friendName: friendName,
        friendAvatar: friendData.friend.avatar_url
      });
    }
  };

  const formatLastSeen = (timestamp) => {
    if (!timestamp) return 'Never';
    try {
      return formatDistanceToNow(new Date(timestamp), { addSuffix: true });
    } catch {
      return 'Unknown';
    }
  };

  // Debounced search - calls API when user stops typing
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (activeTab === 'discover') {
        fetchDiscoveredUsers(searchQuery);
      }
    }, 300); // 300ms debounce

    return () => clearTimeout(timeoutId);
  }, [searchQuery, activeTab]);

  // No local filtering - API does the search
  const filteredDiscovered = discoveredUsers;

  // If chat is selected, show the chat component
  if (selectedChat) {
    return (
      <ThreadChat
        conversationId={selectedChat.conversationId}
        friendId={selectedChat.friendId}
        friendName={selectedChat.friendName}
        friendAvatar={selectedChat.friendAvatar}
        onClose={() => setSelectedChat(null)}
      />
    );
  }

  if (!user?.profile?.completed_questionnaire) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-stone-50 via-white to-amber-50/30 p-4 sm:p-6 lg:p-8">
        <div className="max-w-4xl mx-auto">
          <Card className="card text-center py-12">
            <CardContent>
              <Users className="h-16 w-16 text-gray-400 mx-auto mb-4" />
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">Complete Your Profile First</h2>
              <p className="text-gray-600 mb-6 max-w-md mx-auto">
                To connect with fellow students, please complete your profile with your academic details.
              </p>
              <Button 
                onClick={() => navigate('/profile')}
                className="bg-gradient-to-r from-blue-700 to-blue-800 hover:from-blue-800 hover:to-blue-900"
              >
                Complete Profile
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-stone-50 via-white to-amber-50/30 p-4 sm:p-6 lg:p-8">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900 mb-1">
            Friends & Chat
          </h1>
          <p className="text-gray-600 text-sm">
            Connect and chat with your study partners
          </p>
        </div>

        {/* Tab Navigation */}
        <div className="flex space-x-1 mb-8 bg-gray-100 p-1 rounded-lg w-fit">
          <button
            onClick={() => setActiveTab('friends')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              activeTab === 'friends'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
            data-testid="friends-tab"
          >
            My Friends ({friends.length})
          </button>
          <button
            onClick={() => setActiveTab('requests')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              activeTab === 'requests'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            } relative`}
            data-testid="requests-tab"
          >
            Requests
            {friendRequests.length > 0 && (
              <Badge className="ml-2 bg-blue-700 text-white text-xs">
                {friendRequests.length}
              </Badge>
            )}
          </button>
          <button
            onClick={() => setActiveTab('discover')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              activeTab === 'discover'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
            data-testid="discover-tab"
          >
            Discover
          </button>
        </div>

        {/* Friends Tab */}
        {activeTab === 'friends' && (
          <div className="space-y-6">
            {loading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto"></div>
              </div>
            ) : friends.length === 0 ? (
              <Card className="card text-center py-12">
                <CardContent>
                  <div className="relative inline-block mb-4">
                    <MessageCircle className="h-16 w-16 text-gray-400" />
                    <div className="absolute -top-1 -right-1 bg-yellow-400 rounded-full p-1.5 shadow-lg animate-bounce">
                      <Coins className="h-4 w-4 text-yellow-800" />
                    </div>
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">Connect & Earn AI Credits!</h3>
                  <p className="text-gray-600 max-w-md mx-auto mb-6">
                    Discover fellow students and connect with them.
                  </p>
                  <Button
                    onClick={() => setActiveTab('discover')}
                    className="bg-gradient-to-r from-blue-700 to-blue-800 hover:from-blue-800 hover:to-blue-900 shadow-lg"
                  >
                    <Sparkles className="h-4 w-4 mr-2" />
                    Discover Students
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {friends.map((friendship) => (
                  <Card 
                    key={friendship.id} 
                    className="card hover-lift cursor-pointer"
                    onClick={() => openChat(friendship)}
                    data-testid={`friend-card-${friendship.id}`}
                  >
                    <CardContent className="p-6">
                      <div className="flex items-center space-x-3 mb-4">
                        <Avatar className="w-12 h-12">
                          {friendship.friend?.avatar_url ? (
                            <img 
                              src={friendship.friend.avatar_url} 
                              alt={friendship.friend.name}
                              className="w-full h-full object-cover rounded-full" loading="lazy" decoding="async"/>
                          ) : (
                            <AvatarFallback className="bg-gradient-to-br from-blue-700 to-blue-800 text-white font-semibold">
                              {friendship.friend?.name?.charAt(0).toUpperCase() || 'U'}
                            </AvatarFallback>
                          )}
                        </Avatar>
                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold text-gray-900 truncate">
                            {friendship.friend?.name || 'Unknown User'}
                          </h3>
                          <p className="text-sm text-gray-500">
                            Friends since {formatLastSeen(friendship.created_at)}
                          </p>
                        </div>
                        <MessageCircle className="h-5 w-5 text-orange-500" />
                      </div>

                      <Button
                        className="w-full bg-gradient-to-r from-blue-700 to-blue-800 hover:from-blue-800 hover:to-blue-900"
                        size="sm"
                      >
                        <MessageCircle className="h-4 w-4 mr-2" />
                        Open Chat
                      </Button>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Friend Requests Tab */}
        {activeTab === 'requests' && (
          <div className="space-y-6">
            {friendRequests.length === 0 ? (
              <Card className="card text-center py-12">
                <CardContent>
                  <Users className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">No Pending Requests</h3>
                  <p className="text-gray-600">
                    When students send you friend requests, they'll appear here.
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {friendRequests.map((request) => (
                  <Card key={request.id} className="card">
                    <CardContent className="p-6">
                      <div className="flex items-center space-x-3 mb-4">
                        <Avatar className="w-12 h-12">
                          {request.sender?.avatar_url ? (
                            <img 
                              src={request.sender.avatar_url} 
                              alt={request.sender.name}
                              className="w-full h-full object-cover rounded-full" loading="lazy" decoding="async"/>
                          ) : (
                            <AvatarFallback className="bg-gradient-to-br from-blue-700 to-blue-800 text-white font-semibold">
                              {request.sender?.name?.charAt(0).toUpperCase() || 'U'}
                            </AvatarFallback>
                          )}
                        </Avatar>
                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold text-gray-900 truncate">
                            {request.sender?.name || 'Unknown User'}
                          </h3>
                          <p className="text-xs text-gray-400">
                            {formatLastSeen(request.created_at)}
                          </p>
                        </div>
                      </div>

                      <div className="flex space-x-2">
                        <Button
                          onClick={() => handleAcceptRequest(request.id, request.sender_id, request.sender?.name)}
                          className="flex-1 bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-xs shadow-md"
                          size="sm"
                          data-testid={`accept-request-${request.id}`}
                        >
                          <Coins className="h-4 w-4 mr-1 text-yellow-300" />
                          <CheckCircle className="h-4 w-4 mr-1" />
                          Accept
                        </Button>
                        <Button
                          onClick={() => handleRejectRequest(request.id)}
                          variant="outline"
                          size="sm"
                          className="flex-1 border-red-200 text-red-600 hover:bg-red-50"
                          data-testid={`reject-request-${request.id}`}
                        >
                          <X className="h-4 w-4 mr-1" />
                          Decline
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Discover Tab - Uses MongoDB API */}
        {activeTab === 'discover' && (
          <div className="space-y-6">
            <div className="relative max-w-md">
              <Search className="h-5 w-5 text-gray-400 absolute left-3 top-3" />
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by name, email, mobile, college, company..."
                className="pl-10"
                data-testid="search-users"
              />
            </div>

            {filteredDiscovered.length === 0 ? (
              <Card className="card text-center py-12">
                <CardContent>
                  <div className="relative inline-block mb-4">
                    <Users className="h-16 w-16 text-gray-400" />
                    <div className="absolute -top-1 -right-1 bg-yellow-400 rounded-full p-1.5 shadow-lg">
                      <Coins className="h-4 w-4 text-yellow-800" />
                    </div>
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">
                    {discoveredUsers.length === 0 ? 'No Students Found' : 'No Search Results'}
                  </h3>
                  <p className="text-gray-600">
                    {discoveredUsers.length === 0 
                      ? 'Be the first from your college or program to join and invite friends!'
                      : 'Try different search terms to find more students.'
                    }
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredDiscovered.map((discoveredUser) => (
                  <Card key={discoveredUser.id} className="card">
                    <CardContent className="p-6">
                      <div className="text-center mb-4">
                        <Avatar className="w-16 h-16 mx-auto mb-3">
                          <AvatarFallback className="bg-gradient-to-br from-blue-700 to-blue-800 text-white text-lg font-semibold">
                            {discoveredUser.full_name?.charAt(0).toUpperCase() || 'U'}
                          </AvatarFallback>
                        </Avatar>
                        <h3 className="font-semibold text-gray-900 mb-1">
                          {discoveredUser.full_name}
                        </h3>
                      </div>

                      <div className="space-y-2 mb-4">
                        <div className="text-center">
                          <Badge variant="secondary" className="text-xs">
                            {discoveredUser.college_name}
                          </Badge>
                        </div>
                        <div className="text-center">
                          <Badge variant="outline" className="text-xs">
                            {discoveredUser.degree_name}
                          </Badge>
                        </div>
                      </div>

                      <Button
                        onClick={() => handleSendFriendRequest(discoveredUser.id, discoveredUser.full_name)}
                        className="w-full bg-gradient-to-r from-blue-700 to-blue-800 hover:from-blue-800 hover:to-blue-900 shadow-lg shadow-blue-700/25"
                        size="sm"
                        disabled={sendingRequest[discoveredUser.id]}
                        data-testid={`send-request-${discoveredUser.id}`}
                      >
                        {sendingRequest[discoveredUser.id] ? (
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                        ) : (
                          <>
                            <Coins className="h-4 w-4 mr-1 text-yellow-300" />
                            <UserPlus className="h-4 w-4" />
                          </>
                        )}
                      </Button>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}

        <QuickNav />
      </div>
    </div>
  );
};

export default FriendsWithChat;
